import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import aduPermitImage from '@/assets/adu-permit-timeline.jpg';

const HowLongDoesItTakeToGetADUPermitCalifornia = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/how-long-does-it-take-to-get-adu-permit-california" />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh]">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              ✕
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 py-8">
          {/* Hero Section */}
          <div className="mb-12">
            <img 
              src={aduPermitImage} 
              alt="ADU permit timeline and process" 
              className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
            />
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
              How Long Does It Take to Get an ADU Permit in California?
            </h1>
            
            <p className="text-lg md:text-xl text-white leading-relaxed mb-8">
              Discover exactly how long it takes to get an ADU permit in California. 
              Understand timelines, city-specific variations, and key steps to expedite 
              the permitting process.
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Average Timeline for ADU Permitting in California
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Most California cities process ADU permits within 60-120 days from submission. 
                However, this timeline can vary significantly based on project complexity, 
                local workload, and whether you're using pre-approved plans. Simple ADU projects 
                with pre-approved designs often receive faster approval, sometimes within 30-60 days.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Factors Influencing Permit Approval Time
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Several factors can impact your ADU permit timeline: project complexity, 
                use of pre-approved plans, completeness of application, local department 
                workload, and required inspections. Working with experienced professionals 
                and using standardized designs can significantly reduce processing time.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                City-Specific ADU Permit Timelines (San Jose, Los Angeles, San Diego)
              </h2>
              <p className="text-white leading-relaxed mb-6">
                San Jose typically processes ADU permits in 60-90 days for standard applications. 
                Los Angeles averages 90-120 days, while San Diego ranges from 45-75 days. 
                These timelines can vary based on current caseloads and seasonal fluctuations.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Frequently Asked Questions
              </h2>
              <div className="text-white leading-relaxed space-y-6">
                <div>
                  <p className="font-semibold mb-2">Q: What's the average ADU permit processing time in California?</p>
                  <p>A: Typically, 60–120 days, varying by city and project complexity.</p>
                </div>
                
                <div>
                  <p className="font-semibold mb-2">Q: Can I speed up my ADU permit process?</p>
                  <p>A: Yes, pre-approved ADU plans and professional help can significantly reduce approval time.</p>
                </div>
              </div>
            </section>
          </div>

          {/* Call-to-Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Button 
              onClick={openCalendlyModal}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 text-lg"
            >
              Book & Schedule
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-3 text-lg"
            >
              <Link to="/landing">Learn More</Link>
            </Button>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default HowLongDoesItTakeToGetADUPermitCalifornia;